// Live's undo and redo in the layer editor (spec G13b): 100 steps, each a committed change (an
// add, a text edit, a removal, a restack, a scale, a recentre, a nudge, a finished gesture);
// "Reset all changes" returns to the document as it opened, as one undoable step.
import type { Composition } from "./model";

/** Live's history size. */
export const HISTORY_STEPS = 100;

export interface History {
  /** Where the editor opened: Reset returns here. */
  origin: Composition;
  past: Composition[];
  present: Composition;
  future: Composition[];
}

export function opened(origin: Composition): History {
  return { origin, past: [], present: origin, future: [] };
}

/** A committed change: the present joins the past (the oldest dropped past 100), redo is lost. */
export function committed(history: History, next: Composition): History {
  if (next === history.present) return history;
  const past = [...history.past, history.present].slice(-HISTORY_STEPS);
  return { ...history, past, present: next, future: [] };
}

export function undone(history: History): History {
  const previous = history.past.at(-1);
  if (previous === undefined) return history;
  return {
    ...history,
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
  };
}

export function redone(history: History): History {
  const [next, ...rest] = history.future;
  if (next === undefined) return history;
  return { ...history, past: [...history.past, history.present], present: next, future: rest };
}

/** Live's "Reset all changes": the opening composition, one step. */
export function reset(history: History): History {
  return committed(history, history.origin);
}

/** Whether anything differs from the opening composition (Reset's enabled rule). */
export function changed(history: History): boolean {
  return history.present !== history.origin;
}
